import React, { useState } from 'react';
import { Button, Card, Col, DatePicker, Divider, Form, Input, Modal, Row, Select, Space, Spin, Table, Tag, Timeline, Tooltip, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import Drawer from '../../../components/AppDrawer';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useLead } from '../hooks/useLead';
import { useLeadPipeline } from '../hooks/useLeadPipeline';
import { useUpdateLeadBant } from '../hooks/useUpdateLeadBant';
import { useAddLeadActivity } from '../hooks/useAddLeadActivity';
import type { ActivityType, PipelineStage } from '../types';

dayjs.extend(utc);
import {
  CLOSING_STATUS_LABELS,
  ACTIVITY_TYPE_LABELS,
  BANT_DESCRIPTIONS,
  BANT_SCORE_LABELS,
  BANT_SCORE_OPTIONS,
  PIPELINE_STAGE_LABELS,
  PIPELINE_STAGE_CODE_TO_STAGE,
  HEALTH_STATUS_LABELS,
  REACH_OUT_ACTIVITY_TYPE_LABELS,
  getBantTier,
  closingStatusToNumber,
} from '../constants';
import { useLeadSources } from '../hooks/useLeadSources';
import LeadTasksPanel from './LeadTasksPanel';
import { ACTIVITIES_PANEL_TITLE } from '../../tasks/copy';
import AuditProperties from '../../../components/AuditProperties';

export interface LeadViewDrawerProps {
  open: boolean;
  onClose: () => void;
  leadId: string | null;
  onEdit?: (leadId: string) => void;
}

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '-';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return '-';
  }
}

function formatAmount(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function copyToClipboard(text: string) {
  void navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success('Copied!');
    })
    .catch(() => {
      toast.error('Copy failed');
    });
}

function formatPhoneForTel(phone: string): string {
  const cleaned = phone.trim().replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return `+${cleaned.slice(1).replace(/\+/g, '')}`;
  return cleaned.replace(/\+/g, '');
}

const LeadViewDrawer: React.FC<LeadViewDrawerProps> = ({ open, onClose, leadId, onEdit }) => {
  const { data: lead, isLoading } = useLead(leadId);
  const { data: pipeline } = useLeadPipeline();
  const updateBantMutation = useUpdateLeadBant();
  const addActivityMutation = useAddLeadActivity();
  const { data: sources } = useLeadSources();
  const [bantModalOpen, setBantModalOpen] = useState(false);
  const [activityForm] = Form.useForm<{
    activityType: ActivityType;
    date?: dayjs.Dayjs;
    note?: string;
  }>();
  const [bantForm] = Form.useForm<{
    budgetScore: number;
    authorityScore: number;
    needScore: number;
    timelineScore: number;
  }>();

  const stageLabel = React.useMemo(() => {
    if (!lead) return '-';
    const label = (pipeline?.stages ?? []).find((s) => s.id === lead.pipelineStageId)?.label;
    return label ?? lead.pipelineStageId ?? '-';
  }, [lead, pipeline?.stages]);

  const activityStageLabelBySnapshot = React.useMemo(() => {
    // Map legacy snapshot values (1..7) to the configured pipeline stage label.
    const m: Record<PipelineStage, string> = { ...PIPELINE_STAGE_LABELS };
    for (const s of pipeline?.stages ?? []) {
      if (!Object.prototype.hasOwnProperty.call(PIPELINE_STAGE_CODE_TO_STAGE, s.code)) continue;
      const mappedStage = PIPELINE_STAGE_CODE_TO_STAGE[s.code];
      m[mappedStage] = s.label;
    }
    return m;
  }, [pipeline?.stages]);

  const sourceLabelById = React.useMemo(() => {
    const m: Record<number, string> = {};
    (sources ?? []).forEach(s => { m[s.id] = s.label; });
    return m;
  }, [sources]);


  const handleAddActivity = () => {
    if (!leadId) return;
    activityForm.validateFields().then((values) => {
      const dateStr = values.date
        ? (dayjs.isDayjs(values.date) ? values.date.toISOString() : new Date(values.date).toISOString())
        : undefined;
      addActivityMutation.mutate(
        {
          leadId,
          values: {
            activityType: values.activityType,
            date: dateStr,
            note: values.note,
          },
        },
        {
          onSuccess: () => {
            activityForm.resetFields();
          },
        }
      );
    });
  };

  const handleBantOk = () => {
    if (!leadId) return;
    bantForm.validateFields().then((values) => {
      updateBantMutation.mutate(
        {
          leadId,
          budgetScore: values.budgetScore,
          authorityScore: values.authorityScore,
          needScore: values.needScore,
          timelineScore: values.timelineScore,
        },
        {
          onSuccess: () => {
            setBantModalOpen(false);
          },
        }
      );
    });
  };

  return (
    <>
      <Drawer
        title={lead?.title ?? 'Lead'}
        open={open}
        onClose={onClose}
        width={720}
        destroyOnClose
        extra={
          lead && onEdit ? (
            <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => onEdit(lead.id)}>
              Edit
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : lead ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Header: stage, status, health, key metric */}
            <Card size="small" styles={{ body: { padding: '12px 16px' } }}>
              <Space wrap align="center">
                <Tag color={lead.closingStatus !== 0 ? 'default' : 'blue'}>
                  {stageLabel}
                </Tag>
                {closingStatusToNumber(lead.closingStatus) !== 0 && (
                  <Tag>{CLOSING_STATUS_LABELS[closingStatusToNumber(lead.closingStatus)]}</Tag>
                )}
                {lead.healthStatus != null && lead.healthStatus !== 0 && (
                  <Tag color={lead.healthStatus === 1 ? 'blue' : lead.healthStatus === 2 ? 'orange' : 'default'}>
                    {HEALTH_STATUS_LABELS[lead.healthStatus as keyof typeof HEALTH_STATUS_LABELS]}
                  </Tag>
                )}
                <Divider type="vertical" />
                <Typography.Text strong>
                  System price: {formatAmount(lead.systemPrice)}
                </Typography.Text>
              </Space>
            </Card>

            {/* Summary: people & deal */}
            <Card title="Summary" size="small">
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                People & deal
              </Typography.Text>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Contact
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.contactName ?? '—'}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Assignee
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.assigneeDisplayName ?? '—'}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Source
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {sourceLabelById[lead.sourceId] ?? '—'}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Campaign
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.campaignName ?? '—'}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Referral Contact
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.referralContactName ?? '—'}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Sales Person
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.salesPersonName ?? '—'}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </Card>

            <Card title="Contact" size="small">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Email
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.contactEmail ? (
                      <Space size={4}>
                        <a href={`mailto:${lead.contactEmail}`}>{lead.contactEmail}</a>
                        <CopyOutlined
                          style={{ cursor: 'pointer', opacity: 0.5, fontSize: 11 }}
                          onClick={() => copyToClipboard(lead.contactEmail!)}
                        />
                      </Space>
                    ) : (
                      '—'
                    )}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Mobile phone
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.contactMobileNumber ? (
                      <Space size={4}>
                        <a
                          href={`tel:${formatPhoneForTel(lead.contactMobileNumber)}`}
                        >
                          {lead.contactMobileNumber}
                        </a>
                        <CopyOutlined
                          style={{ cursor: 'pointer', opacity: 0.5, fontSize: 11 }}
                          onClick={() => copyToClipboard(lead.contactMobileNumber!)}
                        />
                      </Space>
                    ) : (
                      '—'
                    )}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    WhatsApp
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {lead.contactWhatsAppNumber ?? '—'}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </Card>

            {/* Pricing & Offerings */}
            <Card title="Pricing & Offerings" size="small">
              <Table
                dataSource={lead.offerings}
                columns={[
                  { title: 'Offering', dataIndex: 'offeringName', key: 'offeringName' },
                  {
                    title: 'Unit price',
                    dataIndex: 'priceAtAddition',
                    key: 'priceAtAddition',
                    render: (v: number) => formatAmount(v),
                  },
                  {
                    title: 'Targeted amount',
                    dataIndex: 'targetedAmount',
                    key: 'targetedAmount',
                    render: (v: number) => formatAmount(v),
                  },
                ]}
                rowKey="offeringId"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}><Typography.Text strong>{formatAmount(lead.systemPrice)}</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}><Typography.Text strong>{formatAmount(lead.targetedAmount)}</Typography.Text></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Closed amount: {lead.closedAmount != null ? formatAmount(lead.closedAmount) : '—'}
              </Typography.Text>
            </Card>

            {/* Timeline */}
            <Card title="Timeline" size="small">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Issue date
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {formatDate(lead.prospectingDate)}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Last contact
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {formatDate(lead.lastContactedAt)}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Est. closing
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {formatDate(lead.estimatedClosingDate)}
                  </Typography.Paragraph>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Actual closing
                  </Typography.Text>
                  <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {formatDate(lead.actualClosingDate)}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </Card>

            {lead.description && (
              <Card title="Description" size="small">
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {lead.description}
                </Typography.Paragraph>
              </Card>
            )}

            {/* BANT */}
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <Space wrap align="center">
                    <span>BANT scores</span>
                    <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                      Guidance only
                    </Typography.Text>
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setBantModalOpen(true)}>
                      Update
                    </Button>
                  </Space>
                  <Tag color={getBantTier(lead.bantTotalScore).color}>
                    Total: {lead.bantTotalScore} ({getBantTier(lead.bantTotalScore).label})
                  </Tag>
                </div>
              }
              size="small"
            >
              <Row gutter={[16, 12]}>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                    Budget
                  </Typography.Text>
                  <Tooltip title={lead.budgetScore === 0 ? 'Unknown' : BANT_DESCRIPTIONS.budget[Math.min(lead.budgetScore - 1, 4)]}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {BANT_SCORE_LABELS[lead.budgetScore] ?? lead.budgetScore} ({lead.budgetScore})
                      <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }} />
                    </span>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                    Authority
                  </Typography.Text>
                  <Tooltip title={lead.authorityScore === 0 ? 'Unknown' : BANT_DESCRIPTIONS.authority[Math.min(lead.authorityScore - 1, 4)]}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {BANT_SCORE_LABELS[lead.authorityScore] ?? lead.authorityScore} ({lead.authorityScore})
                      <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }} />
                    </span>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                    Need
                  </Typography.Text>
                  <Tooltip title={lead.needScore === 0 ? 'Unknown' : BANT_DESCRIPTIONS.need[Math.min(lead.needScore - 1, 4)]}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {BANT_SCORE_LABELS[lead.needScore] ?? lead.needScore} ({lead.needScore})
                      <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }} />
                    </span>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                    Timeline
                  </Typography.Text>
                  <Tooltip title={lead.timelineScore === 0 ? 'Unknown' : BANT_DESCRIPTIONS.timeline[Math.min(lead.timelineScore - 1, 4)]}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {BANT_SCORE_LABELS[lead.timelineScore] ?? lead.timelineScore} ({lead.timelineScore})
                      <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }} />
                    </span>
                  </Tooltip>
                </Col>
              </Row>
            </Card>

            {lead.closingNote && (
              <Card title="Closing note" size="small">
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {lead.closingNote}
                </Typography.Paragraph>
              </Card>
            )}

            {/* Activity timeline */}
            <Card title={ACTIVITIES_PANEL_TITLE} size="small">
              <LeadTasksPanel leadId={lead.id} contactId={lead.contactId} />
            </Card>

            <Card title="Activity timeline" size="small">
              <Timeline
                style={{ marginTop: 8 }}
                items={[...(lead.activities ?? [])]
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((a) => ({
                    children: (
                      <div>
                        <Typography.Text strong>{ACTIVITY_TYPE_LABELS[a.activityType]}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 12 }}>
                          {activityStageLabelBySnapshot[a.pipelineStageSnapshot]} · {dayjs(a.date).format('MMM D, YYYY HH:mm')}
                          {a.userDisplayName && ` · ${a.userDisplayName}`}
                        </Typography.Text>
                        {a.note && (
                          <div style={{ marginTop: 4 }}>
                            <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                              {a.note}
                            </Typography.Paragraph>
                          </div>
                        )}
                      </div>
                    ),
                  }))}
              />
            </Card>

            {/* Log activity */}
            <Card title="Log activity" size="small">
              <Typography.Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 12 }}>
                Record a customer touch without resolving a follow-up task.
              </Typography.Paragraph>
              <Form form={activityForm} layout="vertical" onFinish={handleAddActivity}>
                <Form.Item name="activityType" label="Type" rules={[{ required: true, message: 'Select activity type' }]}>
                  <Select
                    placeholder="e.g. Phone, Meeting"
                    options={Object.entries(REACH_OUT_ACTIVITY_TYPE_LABELS).map(([value, label]) => ({
                      value: Number(value) as ActivityType,
                      label,
                    }))}
                  />
                </Form.Item>
                <Form.Item name="date" label="Date">
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    disabledDate={(current) => current != null && current > dayjs().endOf('day')}
                  />
                </Form.Item>
                <Form.Item name="note" label="Note">
                  <Input.TextArea rows={2} placeholder="What was discussed or agreed?" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={addActivityMutation.isPending}>
                    Log activity
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            <AuditProperties
              values={{
                createdAtUtc: lead.createdAtUtc,
                createdByDisplayName: lead.createdByDisplayName,
                createdByUserId: lead.createdByUserId,
                lastModifiedAtUtc: lead.lastModifiedAtUtc,
                modifiedByDisplayName: lead.modifiedByDisplayName,
                modifiedByUserId: lead.modifiedByUserId,
              }}
            />
          </Space>
        ) : null}
      </Drawer>

      <Modal
        title="Update BANT Scores"
        open={bantModalOpen}
        onOk={handleBantOk}
        onCancel={() => setBantModalOpen(false)}
        confirmLoading={updateBantMutation.isPending}
        afterOpenChange={(opened) => {
          if (opened && lead) {
            bantForm.setFieldsValue({
              budgetScore: lead.budgetScore,
              authorityScore: lead.authorityScore,
              needScore: lead.needScore,
              timelineScore: lead.timelineScore,
            });
          }
        }}
      >
        <Form form={bantForm} layout="vertical">
          <Form.Item name="budgetScore" label="Budget" rules={[{ required: true }]}>
            <Select options={BANT_SCORE_OPTIONS} />
          </Form.Item>
          <Form.Item name="authorityScore" label="Authority" rules={[{ required: true }]}>
            <Select options={BANT_SCORE_OPTIONS} />
          </Form.Item>
          <Form.Item name="needScore" label="Need" rules={[{ required: true }]}>
            <Select options={BANT_SCORE_OPTIONS} />
          </Form.Item>
          <Form.Item name="timelineScore" label="Timeline" rules={[{ required: true }]}>
            <Select options={BANT_SCORE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LeadViewDrawer;
